/**
 * QuantitySelector — Reusable +/- quantity control.
 * Extracted from cart and product pages where it was duplicated.
 */

import { Minus, Plus } from "lucide-react";

type QuantitySelectorProps = {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  size?: "sm" | "md";
};

export function QuantitySelector({
  value,
  onChange,
  min = 1,
  max = 99,
  size = "md",
}: QuantitySelectorProps) {
  const sizes = {
    sm: { button: "min-h-9 w-9", text: "w-8 text-xs" },
    md: { button: "min-h-12 w-12", text: "w-10 text-sm" },
  };

  const s = sizes[size];

  return (
    <div
      className="inline-flex items-center border border-border"
      role="group"
      aria-label="Quantity selector"
    >
      <button
        type="button"
        onClick={() => onChange(Math.max(min, value - 1))}
        disabled={value <= min}
        className={`${s.button} grid place-items-center transition-colors hover:bg-secondary disabled:opacity-40 disabled:cursor-not-allowed`}
        aria-label="Decrease quantity"
      >
        <Minus className="h-4 w-4" />
      </button>
      <span
        className={`${s.text} text-center tabular-nums font-medium select-none`}
        aria-live="polite"
        aria-atomic="true"
      >
        {value}
      </span>
      <button
        type="button"
        onClick={() => onChange(Math.min(max, value + 1))}
        disabled={value >= max}
        className={`${s.button} grid place-items-center transition-colors hover:bg-secondary disabled:opacity-40 disabled:cursor-not-allowed`}
        aria-label="Increase quantity"
      >
        <Plus className="h-4 w-4" />
      </button>
    </div>
  );
}
